import { Controller } from "@hotwired/stimulus"

const GAP = 10

export default class extends Controller {
  connect() {
    this.onResize = () => this.schedule()
    window.addEventListener("resize", this.onResize)
    this.resizeObserver = new ResizeObserver(() => this.schedule())
    this.resizeObserver.observe(this.element)
    this.mutations = new MutationObserver(() => {
      this.force = true
      this.schedule()
    })
    this.observe()
    this.layout()
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
    this.resizeObserver?.disconnect()
    this.mutations?.disconnect()
    clearTimeout(this.timer)
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.layout(), 60)
  }

  layout() {
    const width = Math.floor(this.element.clientWidth)
    if (width < 40) return

    const photos = this.photos()
    if (!photos.length) return
    if (width === this.lastWidth && !this.force) return

    this.lastWidth = width
    this.force = false
    this.render(packRows(photos, width))
  }

  photos() {
    return [...this.element.querySelectorAll("[data-gallery-photo]")].map((el) => {
      const ratio = this.ratioFor(el)
      return { el, ratio }
    })
  }

  ratioFor(el) {
    const width = Number(el.dataset.width)
    const height = Number(el.dataset.height)
    if (width > 0 && height > 0) return width / height

    const img = el.querySelector("img")
    if (img?.naturalWidth > 0 && img.naturalHeight > 0) {
      el.dataset.width = String(img.naturalWidth)
      el.dataset.height = String(img.naturalHeight)
      return img.naturalWidth / img.naturalHeight
    }

    if (img && !img.dataset.ratioWait) {
      img.dataset.ratioWait = "1"
      img.addEventListener("load", () => {
        if (!(img.naturalWidth > 0)) return
        el.dataset.width = String(img.naturalWidth)
        el.dataset.height = String(img.naturalHeight)
        this.force = true
        this.schedule()
      }, { once: true })
    }

    return 4 / 3
  }

  render(rows) {
    this.mutations.disconnect()
    const fragment = document.createDocumentFragment()
    for (const row of rows) {
      const line = document.createElement("div")
      line.className = "flex items-start gap-2.5"
      for (const item of row.items) {
        item.el.style.width = `${item.width}px`
        item.el.style.height = `${item.height}px`
        line.appendChild(item.el)
      }
      fragment.appendChild(line)
    }
    this.element.replaceChildren(fragment)
    this.observe()
  }

  observe() {
    this.mutations.observe(this.element, { childList: true, subtree: true })
  }
}

function targetRowHeight(width) {
  if (width < 700) return 168
  if (width < 1100) return 200
  return 228
}

function fittedHeight(items, width) {
  const gaps = GAP * Math.max(0, items.length - 1)
  const sum = items.reduce((total, item) => total + item.ratio, 0)
  return (width - gaps) / sum
}

function packRows(list, width) {
  const target = targetRowHeight(width)
  const rows = []
  let row = []

  for (const photo of list) {
    const trial = row.concat(photo)
    const withHeight = fittedHeight(trial, width)
    if (row.length && withHeight < target) {
      const withoutHeight = fittedHeight(row, width)
      const closerWithout = Math.abs(withoutHeight - target) <= Math.abs(withHeight - target)
      if (closerWithout) {
        rows.push(row)
        row = [photo]
      } else {
        rows.push(trial)
        row = []
      }
      continue
    }
    row = trial
  }
  if (row.length) rows.push(row)

  return rows.map((items, index) => {
    const last = index === rows.length - 1
    let height = fittedHeight(items, width)
    if (last && height > target) height = target
    return {
      height,
      items: items.map((item) => ({
        el: item.el,
        width: height * item.ratio,
        height
      }))
    }
  })
}
